import {useEffect, useState} from "react"
import axios from "axios"
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from "chart.js"
import {Pie} from "react-chartjs-2"
import {Alert, Badge, Card, Col, Row, Spinner} from "react-bootstrap"
import moment from "moment"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../context/AuthProvider"

ChartJS.register(ArcElement, Tooltip, Legend)

type ExamStatistic = {
    id: string
    nameDe: string
    nameEn: string
    openExaminationRange: string
    closeExaminationRange: string
    fixedDate: string | null
    examEnd: string
    examEnded: boolean
    fixedDateOccurred: boolean
    onDemandParticipants: number
    projectedRegularParticipants: number
    fixedDateParticipants: number | null
}

export default function ExamStatisticsComponent() {
    const auth = useAuth()
    const {t, i18n} = useTranslation("common")
    const [exams, setExams] = useState<ExamStatistic[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    useEffect(() => {
        axios.get<ExamStatistic[]>(
            "/api/statistics/current-exams",
            auth.axiosAuthHeader()
        )
            .then(response => {
                setExams(Array.isArray(response.data) ? response.data : [])
                setError(false)
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [auth])

    return <div className="component">
        <div className="mb-4">
            <h2><i className="bi bi-pie-chart me-2"/>{t("statistics.title")}</h2>
            <p className="text-muted mb-0">{t("statistics.description")}</p>
        </div>

        {error && <Alert variant="danger">{t("statistics.loadError")}</Alert>}
        {loading ? <div className="text-center py-5"><Spinner/></div> : exams.length === 0
            ? <Alert variant="info">{t("statistics.empty")}</Alert>
            : <Row className="g-4">
                {exams.map(exam => {
                    const fixedCount = exam.fixedDateParticipants ?? 0
                    const regularComparisonCount = exam.fixedDateOccurred
                        ? fixedCount
                        : exam.projectedRegularParticipants
                    const examName = i18n.language === "en"
                        ? exam.nameEn || exam.nameDe
                        : exam.nameDe || exam.nameEn
                    const fixedLabel = exam.fixedDateOccurred
                        ? t("statistics.fixedDate")
                        : t("statistics.projectedRegular")

                    return <Col xs={12} lg={6} xl={4} key={exam.id}>
                        <Card className="h-100">
                            <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                                <strong>{examName}</strong>
                                <Badge bg={exam.examEnded ? "secondary" : "success"}>
                                    {exam.examEnded
                                        ? t("statistics.examEnded")
                                        : t("statistics.examOngoing")}
                                </Badge>
                            </Card.Header>
                            <Card.Body className="d-flex flex-column">
                                <div className="mx-auto w-100" style={{maxWidth: 320}}>
                                    <Pie
                                        data={{
                                            labels: [t("statistics.onDemand"), fixedLabel],
                                            datasets: [{
                                                data: [exam.onDemandParticipants, regularComparisonCount],
                                                backgroundColor: ["#00838a", "#f0ad4e"],
                                                borderWidth: 1,
                                            }],
                                        }}
                                        options={{
                                            responsive: true,
                                            plugins: {legend: {position: "bottom"}},
                                        }}
                                    />
                                </div>

                                <div className="d-grid gap-2 mt-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{t("statistics.onDemand")}</span>
                                        <Badge bg="info" text="dark">{exam.onDemandParticipants}</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{t("statistics.fixedDate")}</span>
                                        {exam.fixedDateOccurred
                                            ? <Badge bg="warning" text="dark">{fixedCount}</Badge>
                                            : <Badge bg="secondary">{t("statistics.notOccurred")}</Badge>}
                                    </div>
                                    {!exam.fixedDateOccurred && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>{t("statistics.projectedRegular")}</span>
                                            <Badge bg="warning" text="dark">
                                                {exam.projectedRegularParticipants}
                                            </Badge>
                                        </div>
                                    )}
                                </div>

                                <small className="text-muted mt-3">
                                    {exam.fixedDate
                                        ? t("statistics.fixedDateAt", {
                                            date: moment(exam.fixedDate).format("LLL"),
                                        })
                                        : t("statistics.noFixedDate")}
                                </small>
                                <small className="text-muted">
                                    {t("statistics.bookingPeriod", {
                                        start: moment(exam.openExaminationRange).format("LLL"),
                                        end: moment(exam.closeExaminationRange).format("LLL"),
                                    })}
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                })}
            </Row>}
    </div>
}
